/**
 * Campaigns page — Audiences and Automations.
 *
 * WhatsApp template management (create, edit, submit to Meta, sync, enable/disable) lives in
 * Communications → Templates — that is the single place templates are created, edited, deleted or
 * fetched from. This page only consumes templates (via the API layer in campaignsApi.js) when
 * building an automation; it does not manage them.
 */
import React, { useState } from 'react';
import { Plus, Edit3, Trash2, Users, Zap } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  useListAudiencesQuery,
  useDeleteAudienceMutation,
} from '../../api/campaignsApi';
import AudienceBuilderModal from './AudienceBuilderModal';
import AutomationsTab from './AutomationsTab';
import AutomationModal from './AutomationModal';

export default function CampaignsPage() {
  const [tab, setTab] = useState('audiences');
  const [editingAudience, setEditingAudience] = useState(null); // audience object, or 'new'
  const [editingAutomation, setEditingAutomation] = useState(null); // automation object, or 'new'

  return (
    <>
      <div className="page-header">
        <div className="breadcrumb"><span>Home</span><span className="bc-sep">/</span><span className="bc-cur">Campaigns</span></div>
        <div className="page-header-row">
          <div>
            <h1>Campaigns</h1>
            <div className="page-header-sub">Who receives a message, and when. Manage the templates themselves in Communications.</div>
          </div>
          {tab === 'audiences' ? (
            <button className="btn btn-primary" onClick={() => setEditingAudience('new')}>
              <Plus size={16} /> New audience
            </button>
          ) : (
            <button className="btn btn-primary" onClick={() => setEditingAutomation('new')}>
              <Plus size={16} /> New automation
            </button>
          )}
        </div>
      </div>

      <div className="tabs">
        <button className={`tab-btn ${tab === 'audiences' ? 'active' : ''}`} onClick={() => setTab('audiences')}>
          <Users size={14} /> Audiences
        </button>
        <button className={`tab-btn ${tab === 'automations' ? 'active' : ''}`} onClick={() => setTab('automations')}>
          <Zap size={14} /> Automations
        </button>
      </div>

      {tab === 'audiences' && <AudiencesTab onEdit={setEditingAudience} />}
      {tab === 'automations' && <AutomationsTab onEdit={setEditingAutomation} />}

      {editingAudience && (
        <AudienceBuilderModal
          audience={editingAudience === 'new' ? null : editingAudience}
          onClose={() => setEditingAudience(null)}
        />
      )}
      {editingAutomation && (
        <AutomationModal
          automation={editingAutomation === 'new' ? null : editingAutomation}
          onClose={() => setEditingAutomation(null)}
        />
      )}
    </>
  );
}

/** Saved audiences, each summarised in plain English so the rule is readable without opening it. */
function AudiencesTab({ onEdit }) {
  const { data: audiences, isFetching } = useListAudiencesQuery();
  const [remove] = useDeleteAudienceMutation();

  const removeAudience = async (a) => {
    if (!window.confirm(`Delete the audience "${a.name}"?`)) return;
    try {
      await remove(a.id).unwrap();
      toast.success(`Deleted ${a.name}.`);
    } catch (e) {
      toast.error(e?.data?.error?.message || 'Could not delete the audience.');
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <h3><Users size={15} style={{ verticalAlign: -2, marginRight: 6 }} />Audiences</h3>
        <span className="td-muted" style={{ fontSize: '0.78rem' }}>
          Rules evaluated live against the CRM — never a fixed list, so they always reflect today.
        </span>
      </div>
      <div className="card-body">
        {isFetching && <div className="skeleton" style={{ height: 60 }} />}
        {(audiences ?? []).map((a) => (
          <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
            <span className={`badge ${a.kind === 'Patient' ? 'b-blue' : 'b-purple'}`}>{a.kind}</span>
            <strong style={{ fontSize: '0.85rem' }}>{a.name}</strong>
            <span className="td-muted" style={{ fontSize: '0.8rem' }}>{describeAudience(a)}</span>
            <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 4 }}>
              <button className="btn-icon-sm" title="Edit audience" onClick={() => onEdit(a)}><Edit3 size={14} /></button>
              <button className="btn-icon-sm" title="Delete audience" onClick={() => removeAudience(a)}><Trash2 size={14} /></button>
            </span>
          </div>
        ))}
        {!isFetching && (audiences ?? []).length === 0 && (
          <div className="empty-state">
            <h3>No audiences yet</h3>
            <p>Create one to describe who should receive a message — for example, patients whose last
              appointment was 15 to 20 days ago with nothing booked since.</p>
          </div>
        )}
      </div>
    </div>
  );
}

/** Renders a saved rule as a sentence, mirroring the describe() helper on the Task Rules panel. */
function describeAudience(a) {
  const parts = [];
  const window = (op, from, to, label) => {
    if (!op) return null;
    if (op === 'Between') return `${label} between ${from} and ${to} days ago`;
    if (op === 'Gte') return `${label} at least ${from} days ago`;
    if (op === 'Eq') return `${label} exactly ${from} days ago`;
    return `${label} within the last ${from} days`;
  };
  const opSym = (op) => (op === 'Eq' ? '=' : op === 'Gte' ? '≥' : '≤');

  if (a.kind === 'Patient') {
    const v = window(a.lastVisitOp, a.lastVisitDaysFrom, a.lastVisitDaysTo, 'last visit');
    if (v) parts.push(v);
    if (a.sessionsRemaining != null) parts.push(`sessions left ${opSym(a.sessionsRemainingOp)} ${a.sessionsRemaining}`);
    if (a.minDue != null) parts.push(`owes ≥ ₹${a.minDue}`);
    if (a.patientStatus) parts.push(`status ${a.patientStatus}`);
    if (a.noUpcomingOnly) parts.push('nothing booked');
    if (a.renewalNotPitchedOnly) parts.push('renewal not pitched');
  } else {
    if (a.leadStatus) parts.push(`status ${a.leadStatus}`);
    if (a.leadSource) parts.push(`from ${a.leadSource}`);
    if (a.leadPriority) parts.push(`${a.leadPriority} priority`);
    const f = window(a.followUpOp, a.followUpDaysFrom, a.followUpDaysTo, 'follow-up');
    if (f) parts.push(f);
  }
  if (a.suppressionWindowDays != null) parts.push(`skip if messaged in ${a.suppressionWindowDays}d`);
  return parts.join(' AND ') || 'no conditions';
}
